/**
 * The encounter reducer's dispatch table — the one `switch` binding every leaf command type to
 * its handler in `./`, plus the public bus binding. Each case is a one-line delegation; the
 * per-family handler groups own the bodies, and `./context` owns the shared helpers.
 *
 * The doctrine this tier is written to (loose validation only, each command builds its own
 * inverse, no `ctx.compendium`) lives in `../reducer`, the facade this is reached through.
 */

import { applyWithReducer, CommandError, err, type ApplyCtx, type LeafReducer, type Result } from "@lancer-vtt/shared";
import type { EncounterState } from "../types";
import type { EncounterCommand, EncounterLeafCommand } from "../commands";
import type { EncounterApplyResult, EncounterEvent } from "./context";
import { applyAddCombatant, applyRemoveCombatant, applyRestoreCombatant } from "./lifecycle";
import { applySetHp, applySetHeat, applySetStructure, applySetStress, applySetBurn, applySetAffordanceState,
  applySetWeaponLoaded, applySetCombatantName, applySetConcealed, applySetDisclosure, applySetHpDisclosure, applySetTokenImage } from "./tracked";
import { applyApplyStatus, applyRemoveStatus, applyGrantRuling, applyRestoreStatuses } from "./statuses";
import { applyRenameEncounter, applyEndCombat, applyReopenCombat, applySetGridMode, applyResizeGrid, applySetEnvironment, applySetGround, applySetSitrep } from "./meta";
import { applyEditCombatantBase, applyRestoreCombatantBase } from "./combatantBase";
import { applySetController } from "./control";
import { applySetDoorState, applyRestoreDoorState } from "./doors";
import { applyMoveToken, applyWalkToken, applyPlaceToken, applyTeleportToken, applyRestorePos, applyStandUp, applyRestoreStand } from "./placement";
import { applySetTerrain, applySetEdge, applyApplyTerrainMap } from "./terrainEdits";
import { applySetRoom, applyRemoveRoom } from "./roomEdits";
import { applyAddAnnotation, applyRemoveAnnotation, applySetAnnotation } from "./annotationEdits";
import { applyOrderCombatants, applyActivateUnit, applyEndTurn, applyStampActivated, applyUnstampActivated, applyEndRound, applySetActivation } from "./turns";
import { applyGrantMove, applySpendAction, applySpendReaction, applyRecordMoveReactionDecline, applySetSplitMove, applyRestoreBudget, applyBoost, applyGrantOvercharge, applyGrantActionAllowance, applySpendActionUse } from "./budget";
import { applyOpenAttack, applyCloseAttack, applyConvertBarrageToSkirmish, applyPrepare, applyDropPrepare } from "./attacks";
import { applyApplyResolved } from "./resolved";
import { checkCombatLive } from "../combatOver";

// ── The leaf reducer ───────────────────────────────────────────────────────────

const encounterLeafReducer: LeafReducer<EncounterState, EncounterCommand, EncounterEvent> = (
  state,
  command,
  _ctx,
): Result<EncounterApplyResult, CommandError> => {
  // `command` is already the non-batch leaf (the bus handles `batch`).
  const cmd = command as EncounterLeafCommand;
  // A finished fight refuses the turn and action-economy family (`../combatOver`).
  const live = checkCombatLive(state, cmd.type);
  if (!live.ok) return live;
  switch (cmd.type) {
    // ── combatant lifecycle ────────────────────────────────────────────────────
    case "add_combatant":
      return applyAddCombatant(state, cmd);

    case "remove_combatant":
      return applyRemoveCombatant(state, cmd);

    case "restore_combatant":
      return applyRestoreCombatant(state, cmd);

    // ── tracked-value mutations ────────────────────────────────────────────────
    case "set_hp":
      return applySetHp(state, cmd);

    case "set_heat":
      return applySetHeat(state, cmd);

    case "set_structure":
      return applySetStructure(state, cmd);

    case "set_stress":
      return applySetStress(state, cmd);

    case "set_burn":
      return applySetBurn(state, cmd);

    case "set_affordance_state":
      return applySetAffordanceState(state, cmd);

    case "set_weapon_loaded":
      return applySetWeaponLoaded(state, cmd);

    case "apply_status":
      return applyApplyStatus(state, cmd);

    case "remove_status":
      return applyRemoveStatus(state, cmd);

    case "grant_ruling":
      return applyGrantRuling(state, cmd);

    case "grant_move":
      return applyGrantMove(state, cmd);

    case "restore_statuses":
      return applyRestoreStatuses(state, cmd);

    case "set_combatant_name":
      return applySetCombatantName(state, cmd);

    case "set_concealed":
      return applySetConcealed(state, cmd);

    case "set_disclosure":
      return applySetDisclosure(state, cmd);

    case "set_hp_disclosure":
      return applySetHpDisclosure(state, cmd);

    case "set_token_image":
      return applySetTokenImage(state, cmd);

    // ── encounter-level meta (the brutalist encounter editor: name + grid mode) ────
    case "rename_encounter":
      return applyRenameEncounter(state, cmd);

    case "end_combat":
      return applyEndCombat(state, cmd);

    case "reopen_combat":
      return applyReopenCombat(state, cmd);

    case "set_grid_mode":
      return applySetGridMode(state, cmd);

    case "resize_grid":
      return applyResizeGrid(state, cmd);

    case "set_environment":
      return applySetEnvironment(state, cmd);
    case "set_ground":
      return applySetGround(state, cmd);

    case "set_sitrep":
      return applySetSitrep(state, cmd);

    // ── mutable base ─────────────────────────────────────────────────────────────
    case "edit_combatant_base":
      return applyEditCombatantBase(state, cmd);

    case "restore_combatant_base":
      return applyRestoreCombatantBase(state, cmd);

    // ── control assignment ───────────────────────────────────────────────────────
    case "set_controller":
      return applySetController(state, cmd);

    // ── doors ──────────────────────────────────────────────────────────────────────
    case "set_door_state":
      return applySetDoorState(state, cmd);

    case "restore_door_state":
      return applyRestoreDoorState(state, cmd);

    // ── tactical placement / movement ────────────────────────────────────────────
    case "move_token":
      return applyMoveToken(state, cmd);

    case "walk_token":
      return applyWalkToken(state, cmd);

    case "place_token":
      return applyPlaceToken(state, cmd);

    case "teleport_token":
      return applyTeleportToken(state, cmd);

    case "restore_pos":
      return applyRestorePos(state, cmd);

    case "stand_up":
      return applyStandUp(state, cmd);

    case "restore_stand":
      return applyRestoreStand(state, cmd);

    // ── terrain ───────────────────────────────────────────────────────────────────
    case "set_terrain":
      return applySetTerrain(state, cmd);

    // ── edge walls ────────────────────────────────────────────────────────────────
    case "set_edge":
      return applySetEdge(state, cmd);

    // ── whole-map replace (terrain-editor preset load) ────────────────────────────
    case "apply_terrain_map":
      return applyApplyTerrainMap(state, cmd);

    // ── rooms (a purely-visual primitive) ────────────────────────────────────────
    case "set_room":
      return applySetRoom(state, cmd);

    case "remove_room":
      return applyRemoveRoom(state, cmd);

    // ── annotations (the one player-writable map primitive) ──────────────────────
    case "add_annotation":
      return applyAddAnnotation(state, cmd);

    case "remove_annotation":
      return applyRemoveAnnotation(state, cmd);

    case "set_annotation":
      return applySetAnnotation(state, cmd);

    // ── initiative / turn order ──────────────────────────────────────────────────
    case "order_combatants":
      return applyOrderCombatants(state, cmd);

    case "activate_unit":
      return applyActivateUnit(state, cmd);

    case "end_turn":
      return applyEndTurn(state, cmd);

    case "stamp_activated":
      return applyStampActivated(state, cmd);

    case "unstamp_activated":
      return applyUnstampActivated(state, cmd);

    case "end_round":
      return applyEndRound(state, cmd);

    case "set_activation":
      return applySetActivation(state, cmd);

    case "spend_action":
      return applySpendAction(state, cmd);

    case "spend_reaction":
      return applySpendReaction(state, cmd);

    case "record_move_reaction_decline":
      return applyRecordMoveReactionDecline(state, cmd);
    case "set_split_move":
      return applySetSplitMove(state, cmd);

    case "restore_budget":
      return applyRestoreBudget(state, cmd);

    case "boost":
      return applyBoost(state, cmd);

    case "grant_overcharge":
      return applyGrantOvercharge(state, cmd);

    case "grant_action_allowance":
      return applyGrantActionAllowance(state, cmd);

    case "open_attack":
      return applyOpenAttack(state, cmd);

    case "close_attack":
      return applyCloseAttack(state, cmd);

    case "convert_barrage_to_skirmish":
      return applyConvertBarrageToSkirmish(state, cmd);

    case "prepare":
      return applyPrepare(state, cmd);

    case "drop_prepare":
      return applyDropPrepare(state, cmd);

    // ── action usage limits ──────────────────────────────────────────────────────────
    case "spend_action_use":
      return applySpendActionUse(state, cmd);

    // ── resolution tier ──────────────────────────────────────────────────────────
    case "apply_resolved":
      return applyApplyResolved(state, cmd);

    default:
      return err(
        new CommandError(
          `unknown encounter command type: ${(cmd as { type?: unknown }).type}`,
        ),
      );
  }
};

// ── Public bus binding ─────────────────────────────────────────────────────────

/**
 * Apply an encounter command through the generic command bus. A thin wrapper
 * over `applyWithReducer` bound to {@link encounterLeafReducer} (the roster's
 * `applyRoster` pattern). Atomic `batch`/inverse-composition/undo come free; we
 * write only the leaf cases. `ctx` is accepted for bus uniformity but unused —
 * the manual tier reads no reference data (the frozen base is snapshotted
 * pre-command by a spawn adapter).
 */
export function applyEncounter(
  state: EncounterState,
  command: EncounterCommand,
  ctx: ApplyCtx = {},
): Result<EncounterApplyResult, CommandError> {
  return applyWithReducer({ reducer: encounterLeafReducer, state, command, ctx });
}
